import React from 'react';
import { cn } from '../../../lib/cn';
import { FieldContext, useFieldControl } from './Field';

/**
 * Switch and SwitchRow, the settings toggle.
 *
 * Source selectors: `system.css` `.dsk-switch`, `.dsk-switch-row`,
 * `__text`, `__title`, `__sub`.
 *
 * Doctrine enforced:
 * - ON is a STATE, so the track goes ACCENT, the same rule as
 *   `.dsk-check:checked`. Never ink, which is reserved for commands.
 * - A real `<input type="checkbox">` with `appearance: none`, so the CSS keys
 *   off `:checked` and the platform supplies the keyboard and the semantics.
 * - `role="switch"` is added only when the component is CONTROLLED, because the
 *   role requires an accurate `aria-checked` and an uncontrolled input has no
 *   value React can read. Uncontrolled falls back to plain checkbox semantics,
 *   which are correct, just less specific in the announcement.
 * - A switch commits immediately, so it never sits next to a Save button for
 *   the same value.
 */
export const Switch = React.forwardRef(function Switch({ className, children, ...rest }, ref) {
  const field = useFieldControl(rest);

  // `children` is pulled out and dropped on purpose. This renders a bare
  // <input>, which is a void element, so letting children reach it through the
  // spread throws "input is a void element tag" and takes down the whole tree.
  // Most component libraries accept <Switch>Label</Switch>, so it is an easy
  // mistake to make. The label belongs on SwitchRow or a Label instead.
  if (process.env.NODE_ENV !== 'production' && children != null) {
    // eslint-disable-next-line no-console
    console.warn(
      'Switch does not render children. Wrap it in <SwitchRow title="..."> or pair it with a <Label>.',
    );
  }

  // Built as an object rather than written inline so the role and its required
  // aria-checked can never be emitted apart from each other.
  const switchRole =
    rest.checked === undefined ? {} : { role: 'switch', 'aria-checked': !!rest.checked };

  return (
    <input
      ref={ref}
      type="checkbox"
      className={cn('dsk-switch', className)}
      {...switchRole}
      {...rest}
      id={field.id}
      aria-describedby={field['aria-describedby']}
    />
  );
});
Switch.displayName = 'Switch';

/**
 * SwitchRow, `.dsk-switch-row`. Label plus description on the left, the switch
 * pushed right by the CSS's own `margin-left: auto`.
 *
 * The row mints the control id and hands it down through `FieldContext`, so a
 * bare `<Switch />` child is really associated with the visible title. Clicking
 * the title toggles the switch, which is why `.dsk-switch-row__title` carries
 * `cursor: pointer` upstream.
 *
 *   <SwitchRow title="Sold alerts" sub="Email me when a listing sells">
 *     <Switch checked={on} onChange={...} />
 *   </SwitchRow>
 */
export const SwitchRow = React.forwardRef(function SwitchRow(
  { title, sub, id, children, className, ...rest },
  ref,
) {
  const uid = React.useId();
  const controlId = id || `${uid}-switch`;
  const ctx = React.useMemo(
    () => ({ controlId, labelId: `${uid}-label`, describedBy: sub ? `${uid}-sub` : undefined }),
    [controlId, uid, sub],
  );

  return (
    <div ref={ref} className={cn('dsk-switch-row', className)} {...rest}>
      <div className="dsk-switch-row__text">
        <label className="dsk-switch-row__title" id={`${uid}-label`} htmlFor={controlId}>
          {title}
        </label>
        {sub ? (
          <span className="dsk-switch-row__sub" id={`${uid}-sub`}>
            {sub}
          </span>
        ) : null}
      </div>
      <FieldContext.Provider value={ctx}>{children}</FieldContext.Provider>
    </div>
  );
});
SwitchRow.displayName = 'SwitchRow';

export default Switch;
